import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';

@Component({
  selector: 'app-group-select',
  templateUrl: './group-dropdown.component.html',
  styleUrls: ['./group-dropdown.component.scss'],
  standalone: false,
})
export class GroupDropdownComponent {
  @Input() disabled: boolean = false; // Disable the dropdown
  @Input() groups: any[] = []; // List of groups
  @Input() groupIdProp: string = 'id'; // Field representing the group id
  @Input() groupDisplayProp: string = 'name'; // Field to display for groups
  @Input() groupItemsProp: string = 'items'; // Field representing the items in the group
  @Input() itemIdProp: string = 'items'; // Field representing the items
  @Input() itemDisplayProp: string = 'name'; // Field to display for items
  @Input() selectedItems: any[] = []; // List of selected items
  @Input() hideGroupSelection: boolean = false; // Hide group selection

  @Output() groupSelected = new EventEmitter<any>();
  @Output() groupRemoved = new EventEmitter<any>();
  @Output() itemSelected = new EventEmitter<any>();
  @Output() itemRemoved = new EventEmitter<any>();

  isOpen = false;
  searchTerm = '';
  showAddedInstruments = false;
  showOnlyReadyInstruments = true;

  constructor(private elementRef: ElementRef) {}

  @HostListener('document:click', ['$event'])
  onFocusOut(event: MouseEvent): void {
    // Check if the click is outside the dropdown
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.isOpen = false; // Close the dropdown only if the click is outside
      this.searchTerm = '';
    }
  }

  // Toggle dropdown
  toggleDropdown(event: MouseEvent): void {
    if (this.disabled) {
      return;
    }

    event.stopPropagation(); // Avoid event bubbling
    this.isOpen = !this.isOpen;

    if (!this.isOpen) {
      this.searchTerm = '';
    }
  }

  // Toggle group
  toggleGroup(event: MouseEvent, group: any): void {
    event.stopPropagation(); // Avoid event bubbling
    group.expanded = !group.expanded;
  }

  // Select group
  selectGroup(event: MouseEvent, group: any): void {
    event.stopPropagation(); // Avoid event bubbling
    this.groupSelected.emit(group);
  }

  // Remove group
  removeGroup(event: MouseEvent, group: any): void {
    event.stopPropagation(); // Avoid event bubbling
    this.groupRemoved.emit(group);
  }

  // Select item
  selectItem(event: MouseEvent, item: any): void {
    event.stopPropagation(); // Avoid event bubbling
    this.itemSelected.emit(item);
  }

  // Remove selected item
  removeItem(event: MouseEvent, item: any): void {
    event.stopPropagation(); // Avoid event bubbling
    this.itemRemoved.emit(item);
  }

  shouldShowGroup(group: any): boolean {
    const items = group?.[this.groupItemsProp] ?? [];

    if (items.length === 0) {
      return false;
    }

    const hasVisibleItems = items.some((item: any) => this.shouldShowItem(item));
    if (!hasVisibleItems) {
      return false;
    }

    if (!this.normalizedSearchTerm) {
      return true;
    }

    const groupMatchesSearch = this.matchesSearch(group?.[this.groupDisplayProp]);
    if (groupMatchesSearch) {
      return true;
    }

    return items.some((item: any) => this.shouldShowItem(item));
  }

  shouldShowItem(item: any): boolean {
    const shouldIncludeSelected = this.showAddedInstruments || !this.isItemSelected(item);
    const shouldIncludeByRequiredFiles = !this.showOnlyReadyInstruments || this.hasRequiredFiles(item);
    return shouldIncludeSelected && shouldIncludeByRequiredFiles && this.matchesSearch(item?.[this.itemDisplayProp]);
  }

  getVisibleItemsCount(group: any): number {
    const items = group?.[this.groupItemsProp] ?? [];
    return items.filter((item: any) => this.shouldShowItem(item)).length;
  }

  getEmptyGroupMessage(group: any): string {
    if (this.normalizedSearchTerm && this.getVisibleItemsCount(group) === 0) {
      return 'No instruments match the current search.';
    }

    if (!this.showAddedInstruments && this.getAvailableItemsCount(group) === 0) {
      return 'All instruments in this group are already selected.';
    }

    return '';
  }

  hasVisibleGroups(): boolean {
    return this.groups.some((group: any) => this.shouldShowGroup(group));
  }

  clearSearch(event: MouseEvent): void {
    event.stopPropagation();
    this.searchTerm = '';
  }

  isAlreadySelected(item: any): boolean {
    return this.isItemSelected(item);
  }

  getDropdownLabel(): string {
    const totalGroups = this.getTotalGroupsCount();
    const totalInstruments = this.getTotalInstrumentsCount();

    if (totalGroups === 0 && totalInstruments === 0) {
      return 'Browse instrument groups';
    }

    return `${totalGroups} group${totalGroups === 1 ? '' : 's'} • ${totalInstruments} instrument${totalInstruments === 1 ? '' : 's'} total`;
  }

  getTotalGroupsCount(): number {
    return this.groups.length;
  }

  getTotalInstrumentsCount(): number {
    return this.groups.reduce((count, group) => {
      const items = group?.[this.groupItemsProp] ?? [];
      return count + items.length;
    }, 0);
  }

  getAvailableItemsCount(group: any): number {
    const items = group?.[this.groupItemsProp] ?? [];
    return items.filter((item: any) => !this.isItemSelected(item)).length;
  }

  getSelectedItemsCount(group: any): number {
    const items = group?.[this.groupItemsProp] ?? [];
    return items.filter((item: any) => this.isItemSelected(item)).length;
  }

  private isItemSelected(item: any): boolean {
    return this.selectedItems.some(
      (selectedItem: any) => selectedItem[this.itemIdProp] === item[this.itemIdProp],
    );
  }

  private matchesSearch(value: any): boolean {
    if (!this.normalizedSearchTerm) {
      return true;
    }

    return String(value ?? '').toLowerCase().includes(this.normalizedSearchTerm);
  }

  private get normalizedSearchTerm(): string {
    return this.searchTerm.trim().toLowerCase();
  }

  private hasRequiredFiles(item: any): boolean {
    return !!item?.hasLUTFile && !!item?.hasFactoryCalibrationFile;
  }
}
